import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api, type CallbackListFilters } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { useActiveIntegrator } from '../auth/active-integrator'

const STATUSES = ['pending', 'sent', 'permanently_failed']
const EVENTS = [
  'invoice.nav.accepted',
  'invoice.nav.rejected',
  'invoice.nav.failed',
]
const PAGE_SIZE = 50

export function CallbacksPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [offset, setOffset] = useState(0)
  const { session } = useAuth()
  const active = useActiveIntegrator()

  const scopedIntegratorId: string | undefined =
    session && !session.isSystemAdmin
      ? active.integratorId
      : active.isOverride
        ? active.integratorId
        : (searchParams.get('integratorId') ?? undefined)

  const canEditIntegratorFilter = !!session?.isSystemAdmin && !active.isOverride

  const filters = useMemo<CallbackListFilters>(
    () => ({
      integratorId: scopedIntegratorId,
      status: searchParams.get('status') ?? undefined,
      eventName: searchParams.get('eventName') ?? undefined,
      dateFrom: searchParams.get('dateFrom') ?? undefined,
      dateTo: searchParams.get('dateTo') ?? undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [searchParams, offset, scopedIntegratorId],
  )

  const { data, isLoading, error } = useQuery({
    queryKey: ['callbacks', filters],
    queryFn: () => api.callbacks.list(filters),
  })

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
    setOffset(0)
  }

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold">Callbacks</h1>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {canEditIntegratorFilter && (
          <FilterInput label="Integrator ID" value={searchParams.get('integratorId') ?? ''} onChange={(v) => setFilter('integratorId', v)} />
        )}
        <FilterSelect label="Status" value={searchParams.get('status') ?? ''} onChange={(v) => setFilter('status', v)} options={STATUSES} />
        <FilterSelect label="Event" value={searchParams.get('eventName') ?? ''} onChange={(v) => setFilter('eventName', v)} options={EVENTS} />
        <FilterInput type="datetime-local" label="Created from" value={searchParams.get('dateFrom') ?? ''} onChange={(v) => setFilter('dateFrom', v)} />
        <FilterInput type="datetime-local" label="Created to" value={searchParams.get('dateTo') ?? ''} onChange={(v) => setFilter('dateTo', v)} />
      </div>

      {isLoading && <div>Loading callbacks…</div>}
      {error && <div className="text-red-600">Failed to load: {(error as Error).message}</div>}

      {data && (
        <>
          <div className="mb-2 text-xs text-slate-500">
            {data.total.toLocaleString()} total · showing {offset + 1}–{offset + data.items.length}
          </div>
          <div className="overflow-hidden rounded border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs font-semibold uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Integrator</th>
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">Invoice</th>
                  <th className="px-3 py-2">Delivery</th>
                  <th className="px-3 py-2 text-right">Attempts</th>
                  <th className="px-3 py-2">Sent at</th>
                  <th className="px-3 py-2">Last error</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((cb) => (
                  <tr key={cb.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 text-xs text-slate-500">
                      <Link to={`/callbacks/${cb.id}`} className="text-brand-700 hover:underline">
                        {new Date(cb.createdAt).toLocaleString()}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{cb.integratorName ?? cb.integratorId.slice(0, 8)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{cb.eventName ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {cb.invoiceId ? (
                        <Link to={`/invoices/${cb.invoiceId}`} className="text-brand-700 hover:underline">
                          {cb.invoiceNumber ?? cb.invoiceId.slice(0, 8)}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <StatusChip status={cb.status} sentAt={cb.sentAt} />
                    </td>
                    <td className="px-3 py-2 text-right">{cb.attemptCount}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {cb.sentAt ? new Date(cb.sentAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-red-700">{cb.lastErrorPreview ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-between text-sm">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
            >
              ← previous
            </button>
            <button
              type="button"
              disabled={offset + data.items.length >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
            >
              next →
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function FilterInput(props: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="text-xs text-slate-600">
      {props.label}
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
      />
    </label>
  )
}

function FilterSelect(props: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="text-xs text-slate-600">
      {props.label}
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
      >
        <option value="">(any)</option>
        {props.options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  )
}

function StatusChip({ status, sentAt }: { status: string; sentAt: string | null }) {
  // Delivery status = "received on the other side" if the row is `sent`
  // AND a sent_at timestamp exists (dispatcher only sets both together).
  const delivered = status === 'sent' && !!sentAt
  const cls = delivered
    ? 'bg-emerald-100 text-emerald-800'
    : status === 'permanently_failed'
    ? 'bg-red-100 text-red-800'
    : 'bg-amber-100 text-amber-800'
  const label = delivered ? 'delivered' : status
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
}
